// ─────────────────────────────────────────────────────────────
// useExchangeRates.js — Taux de change automatiques
// Source : Frankfurter API v2 (api.frankfurter.dev) — gratuit,
// sans clé, hébergé derrière Cloudflare (CORS natif, fiable).
//
// ⚠️ HISTORIQUE : ce fichier utilisait auparavant l'ancien domaine
// api.frankfurter.app, qui a migré vers api.frankfurter.dev/v2.
// L'ancien domaine reste techniquement en ligne mais son uptime
// mesuré est dégradé (~86% sur 90 jours d'après le monitoring
// public), ce qui causait des "Failed to fetch" intermittents.
//
// XOF = Franc CFA Ouest-Africain (parité FIXE avec EUR, jamais
// interrogée en ligne — voir FCFA_PAR_EUR_FIXE ci-dessous).
// ─────────────────────────────────────────────────────────────
import { useEffect } from 'react';

const CACHE_KEY   = 'kotos_exchange_rates';
const CACHE_HOURS = 24; // Rafraîchir toutes les 24h max

// ── Parité légale FIXE EUR/XOF ───────────────────────────────
// 1 EUR = 655.957 FCFA — fixée par traité depuis 1999, ne bouge
// JAMAIS. Ce n'est pas un taux de marché : aucune API ne le publie
// comme taux flottant, et il ne faut jamais l'interroger en ligne.
const FCFA_PAR_EUR_FIXE = 655.957;

export function useExchangeRates(data, updateData) {
  useEffect(() => {
    fetchAndUpdateRates(data, updateData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

async function fetchAndUpdateRates(data, updateData) {
  try {
    // ── Vérifier le cache localStorage ──────────────────────
    const cached = localStorage.getItem(CACHE_KEY);
    if (cached) {
      const { rates, timestamp } = JSON.parse(cached);
      const heuresEcoulees = (Date.now() - timestamp) / (1000 * 60 * 60);
      if (heuresEcoulees < CACHE_HOURS) {
        applyRates(rates, data, updateData, true);
        return;
      }
    }

    // ── Appel API Frankfurter v2 — endpoint "rate" unique ────
    // Documentation officielle : https://frankfurter.dev/
    // Exemple exact recommandé par la doc pour une seule paire.
    const response = await fetch(
      'https://api.frankfurter.dev/v2/rate/eur/usd',
      { signal: AbortSignal.timeout(6000) }
    );

    if (!response.ok) throw new Error('API indisponible (v2)');

    const json = await response.json();
    const eurVersUsd = Number(json.rate) || 1.08; // 1 EUR = X USD

    const rates = {
      eurParFcfa:     1 / FCFA_PAR_EUR_FIXE,
      usdParFcfa:     eurVersUsd / FCFA_PAR_EUR_FIXE,
      fcfaParEur:     FCFA_PAR_EUR_FIXE,
      fcfaParUsd:     FCFA_PAR_EUR_FIXE / eurVersUsd,
      lastUpdate:     new Date().toLocaleDateString('fr-FR'),
      lastUpdateFull: new Date().toLocaleString('fr-FR'),
      source:         'Frankfurter v2 (api.frankfurter.dev) · XOF parité fixe',
    };

    // ── Sauvegarder en cache ─────────────────────────────────
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      rates,
      timestamp: Date.now(),
    }));

    applyRates(rates, data, updateData, true);

    console.log(`✅ Taux mis à jour : 1€ = ${rates.fcfaParEur.toFixed(3)} FCFA (fixe) · 1$ = ${rates.fcfaParUsd.toFixed(2)} FCFA`);

  } catch (err) {
    console.warn('⚠️ Taux USD non actualisé :', err.message, '— parité EUR/FCFA reste fixe et fiable.');

    const fallback = {
      eurParFcfa:     1 / FCFA_PAR_EUR_FIXE,
      usdParFcfa:     0.00162, // ≈ 1$ = 617 FCFA (dernière valeur connue, approximative)
      fcfaParEur:     FCFA_PAR_EUR_FIXE,
      fcfaParUsd:     617,
      lastUpdate:     data.tauxChange?.lastUpdate     || '',
      lastUpdateFull: data.tauxChange?.lastUpdateFull || '',
      source:         'Fallback — USD approximatif (XOF toujours fixe et exact)',
    };

    applyRates(fallback, data, updateData, false);
  }
}

function applyRates(rates, data, updateData, auto) {
  const current = data.tauxChange || {};

  const sameRates =
    Math.abs((current.eurParFcfa || 0) - rates.eurParFcfa) < 0.000001 &&
    Math.abs((current.usdParFcfa || 0) - rates.usdParFcfa) < 0.000001;
  const sameAuto = current.auto === auto;

  if (sameRates && sameAuto) return;

  const newTaux = {
    ...current,
    ...rates,
    auto,
  };

  updateData({ tauxChange: newTaux });
}